import * as React from 'react';

import { cn } from '@rematter/ui-web/lib';

import { VariableRecord } from '../../types/pricing';

type PriceFieldProps = {
  value: string;
  onChange: (value: string) => void;
  variables: VariableRecord[];
  placeholder?: string;
  className?: string;
  isDisabled?: boolean;
};

//
// ⚛️ ⚛️ ⚛️ PRICE FIELD ⚛️ ⚛️ ⚛️
//
export const PriceField = ({
  value,
  onChange,
  variables,
  placeholder,
  className,
  isDisabled,
}: PriceFieldProps) => {
  // Refs
  const inputRef = React.useRef<HTMLInputElement>(null);

  // States
  const [isOpen, setIsOpen] = React.useState(false);
  const [highlightIndex, setHighlightIndex] = React.useState(0);
  const [dollarPos, setDollarPos] = React.useState<number | null>(null);

  // Memoized values
  const searchTerm = React.useMemo(() => {
    if (dollarPos == null) return '';
    const pos = inputRef.current?.selectionStart ?? value.length;
    return value.slice(dollarPos + 1, pos).toLowerCase();
  }, [value, dollarPos]);

  const filtered = React.useMemo(() => {
    if (!isOpen) return [];
    return variables.filter((v) => v.name.toLowerCase().startsWith(searchTerm));
  }, [variables, searchTerm, isOpen]);

  // Callbacks
  const insertVariable = React.useCallback(
    (varName: string) => {
      if (dollarPos == null) return;
      const pos = inputRef.current?.selectionStart ?? value.length;
      const before = value.slice(0, dollarPos);
      const after = value.slice(pos);
      const newValue = `${before}$${varName}${after}`;
      onChange(newValue);
      setIsOpen(false);
      setDollarPos(null);
      requestAnimationFrame(() => {
        const newPos = dollarPos + 1 + varName.length;
        inputRef.current?.setSelectionRange(newPos, newPos);
        inputRef.current?.focus();
      });
    },
    [dollarPos, value, onChange],
  );

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      const pos = e.target.selectionStart ?? newValue.length;
      onChange(newValue);

      if (pos > 0 && newValue[pos - 1] === '$') {
        setDollarPos(pos - 1);
        setIsOpen(true);
        setHighlightIndex(0);
      } else if (isOpen && dollarPos != null) {
        const partial = newValue.slice(dollarPos + 1, pos);
        if (/^[a-zA-Z0-9_]*$/.test(partial)) {
          setHighlightIndex(0);
        } else {
          setIsOpen(false);
          setDollarPos(null);
        }
      }
    },
    [onChange, isOpen, dollarPos],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen || filtered.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setHighlightIndex((i) => (i + 1) % filtered.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setHighlightIndex((i) => (i - 1 + filtered.length) % filtered.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        insertVariable(filtered[highlightIndex].name);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(false);
        setDollarPos(null);
      }
    },
    [isOpen, filtered, highlightIndex, insertVariable],
  );

  const handleBlur = React.useCallback(() => {
    setTimeout(() => {
      setIsOpen(false);
      setDollarPos(null);
    }, 150);
  }, []);

  // Effects
  React.useEffect(() => {
    setHighlightIndex(0);
  }, [searchTerm]);

  return (
    <div className="@PriceField relative w-full">
      <input
        ref={inputRef}
        type="text"
        className={cn(
          'w-full rounded border border-neutral/30 bg-transparent px-2 py-1 text-sm focus:border-primary focus:outline-none',
          className,
        )}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={isDisabled}
      />
      {isOpen && filtered.length > 0 && (
        <div className="absolute top-full left-0 z-50 mt-1 max-h-48 w-56 overflow-y-auto rounded-md border border-neutral/30 bg-white py-1 shadow-md">
          {filtered.map((v, i) => (
            <button
              key={v.id}
              className={cn(
                'flex w-full items-center justify-between px-3 py-1.5 text-left text-sm',
                i === highlightIndex && 'bg-primary/10',
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                insertVariable(v.name);
              }}
              onMouseEnter={() => setHighlightIndex(i)}
            >
              <span className="font-mono text-primary">${v.name}</span>
              <span className="text-xs text-neutral/60">
                {v.value != null ? v.value : '—'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
